// iter246 (2026-08-22) — Admin Panel offline-first cache.
//
// Owner directive: the Admin Panel must feel instant even on slow / no
// internet.  This module is a very small IndexedDB wrapper used to
// stale-while-revalidate cache lists like customers, bookings, quotes,
// and recently-viewed customer detail pages.
//
// Design goals:
//   * ZERO third-party dependencies.
//   * Single object store `kv` keyed by string.
//   * Every entry stores `{ key, value, savedAt }` so callers can render
//     "Last synced 2 min ago" copy.
//   * SAFE by construction: never cache credit-card numbers, Stripe
//     secrets, API keys, passwords, or auth tokens — the caller decides
//     which keys to persist, and Admin.jsx only persists lists of
//     domain objects.
//   * Fail-open: on browsers without IndexedDB (private mode, ancient
//     Safari, iframes with disabled storage) every method returns `null`
//     and the caller falls back to network-only.

const DB_NAME = "jdsn_admin_cache_v1";
const STORE = "kv";

function _openDB() {
  return new Promise((resolve) => {
    if (typeof indexedDB === "undefined") return resolve(null);
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function _tx(mode, run) {
  const db = await _openDB();
  if (!db) return null;
  return new Promise((resolve) => {
    let out = null;
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    try {
      out = run(store);
    } catch {
      /* swallow */
    }
    tx.oncomplete = () => resolve(out);
    tx.onerror = () => resolve(null);
    tx.onabort = () => resolve(null);
  });
}

// -------- Public API --------

/**
 * Read a cached entry.  Returns `{ value, savedAt }` or `null`.
 */
export async function getCache(key) {
  return _tx("readonly", (store) => {
    return new Promise((resolve) => {
      const req = store.get(key);
      req.onsuccess = () => {
        const rec = req.result;
        resolve(rec ? { value: rec.value, savedAt: rec.savedAt } : null);
      };
      req.onerror = () => resolve(null);
    });
  }).then((p) => (p && typeof p.then === "function" ? p : p));
}

// Because of the tx wrapper the promise above resolves before store.get()
// completes.  Re-implement without the extra tx wrapper so callers get
// the actual value.
export async function readCache(key) {
  const db = await _openDB();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readonly");
      const store = tx.objectStore(STORE);
      const req = store.get(key);
      req.onsuccess = () => {
        const rec = req.result;
        resolve(rec ? { value: rec.value, savedAt: rec.savedAt } : null);
      };
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/**
 * Persist a value under `key`.  Stores the wall-clock timestamp so the
 * UI can show a "Last synced" indicator.
 */
export async function writeCache(key, value) {
  const db = await _openDB();
  if (!db) return false;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      store.put({ value, savedAt: Date.now() }, key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

/**
 * Convenience: run `network()` with a timeout while showing stale
 * cache immediately.  Signature:
 *
 *   swr(key, network, { onCached, onFresh, timeoutMs })
 *
 * `network` MUST return the value to persist (already normalised).
 * Errors during `network` do not throw — the cached value stays.
 * Returns an object `{ cached, promise }` where `promise` resolves with
 * `{ ok, value, error }`.
 */
export async function swr(key, network, opts = {}) {
  const { onCached, onFresh, timeoutMs = 12000 } = opts;
  const cached = await readCache(key);
  if (cached && onCached) {
    try { onCached(cached.value, cached.savedAt); } catch { /* noop */ }
  }
  const promise = (async () => {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      const value = await Promise.race([
        network(controller.signal),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("timeout")), timeoutMs)
        ),
      ]).finally(() => clearTimeout(timer));
      await writeCache(key, value);
      if (onFresh) {
        try { onFresh(value, Date.now()); } catch { /* noop */ }
      }
      return { ok: true, value, error: null };
    } catch (error) {
      return { ok: false, value: cached?.value ?? null, error };
    }
  })();
  return { cached, promise };
}

/**
 * "Last synced" formatter used by the header status pill.
 */
export function formatSyncedAgo(savedAt) {
  if (!savedAt) return "";
  const secs = Math.max(0, Math.round((Date.now() - savedAt) / 1000));
  if (secs < 5) return "just now";
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? "" : "s"} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}
